// Small pieces shared by the ASO.Market page: money, status pills, progress,
// flags, the order in words, and the form controls the order modal uses.
import type { ReactNode } from 'react';
import type { AsomCampaign } from '@/api/asoMarket';
import { COUNTRY_MAP } from '@/lib/countries';

export const usd = (n: number | null | undefined, digits = 2) =>
  n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

/** SQL 'YYYY-MM-DD HH:MM:SS' (UTC) or ISO → Date. */
export function toDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const d = new Date(raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDate(raw: string | null | undefined, withTime = true): string {
  const d = toDate(raw);
  if (!d) return '—';
  return d.toLocaleString('en-GB', withTime
    ? { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: 'short', year: 'numeric' });
}

export function ago(raw: string | null | undefined): string {
  const d = toDate(raw);
  if (!d) return 'never';
  const min = Math.round((Date.now() - d.getTime()) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  if (min < 48 * 60) return `${Math.round(min / 60)} h ago`;
  return `${Math.round(min / 1440)} d ago`;
}

export function hoursText(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${Math.round(h * 10) / 10} h`;
  return `${Math.round(h / 24 * 10) / 10} days`;
}

export const flag = (code: string) => COUNTRY_MAP[code]?.flag ?? '🏳️';

export function Flags({ codes, max = 6 }: { codes: string[]; max?: number }) {
  if (!codes.length) return <span className="text-content-subtle">—</span>;
  const shown = codes.slice(0, max);
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs text-content-muted" title={codes.map((c) => COUNTRY_MAP[c]?.name ?? c).join(', ')}>
      {shown.map((c) => <span key={c} className="whitespace-nowrap">{flag(c)} {c}</span>)}
      {codes.length > max && <span className="text-content-subtle">+{codes.length - max}</span>}
    </span>
  );
}

export function orderWords(c: Pick<AsomCampaign, 'type' | 'rating' | 'add_text' | 'search_keyword_type' | 'search_keyword'>): { what: string; how: string | null } {
  const what = c.type === 3
    ? 'Installs'
    : c.add_text ? `Reviews${c.rating ? ` ${c.rating}★` : ''}` : `${c.rating ? `${c.rating}★ ` : ''}Ratings`;
  const how = c.search_keyword_type === 2 && c.search_keyword
    ? `via “${c.search_keyword}”`
    : c.search_keyword_type === 1 ? 'via app name search' : null;
  return { what, how };
}

const TYPE_TONE: Record<string, string> = {
  Installs: 'bg-info-soft text-info',
  Reviews: 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200',
  Ratings: 'bg-warning-soft text-warning',
};

export function TypeBadge({ campaign }: { campaign: Parameters<typeof orderWords>[0] }) {
  const { what } = orderWords(campaign);
  const key = what.startsWith('Installs') ? 'Installs' : what.startsWith('Reviews') ? 'Reviews' : 'Ratings';
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${TYPE_TONE[key]}`}>{what}</span>;
}

export interface StatusInfo { label: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'muted' }

export function statusInfo(c: Pick<AsomCampaign, 'state' | 'status'>): StatusInfo {
  if (c.state === 'planned') return { label: 'Queued', tone: 'info' };
  if (c.state === 'held') return { label: 'Over cap: waits', tone: 'warning' };
  if (c.state === 'skipped') return { label: 'Skipped', tone: 'muted' };
  if (c.state === 'submitting') return { label: 'Sending', tone: 'info' };
  if (c.state === 'unknown') return { label: 'No answer yet', tone: 'warning' };
  if (c.state === 'rejected') return { label: 'Refused', tone: 'danger' };
  if (c.state === 'lost') return { label: 'Lost', tone: 'danger' };
  switch (c.status) {
    case 10: return { label: 'Awaiting payment', tone: 'warning' };
    case 20: return { label: 'Running', tone: 'info' };
    case 30: return { label: 'Finished', tone: 'success' };
    case 100: return { label: 'Cancelled', tone: 'muted' };
    default: return { label: '—', tone: 'muted' };
  }
}

const PILL: Record<StatusInfo['tone'], string> = {
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
  muted: 'bg-surface-muted text-content-muted',
};

export function StatusPill({ campaign }: { campaign: Pick<AsomCampaign, 'state' | 'status'> }) {
  const s = statusInfo(campaign);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${PILL[s.tone]}`}>
      {s.tone === 'info' && campaign.status === 20 && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
      {s.label}
    </span>
  );
}

export const CANCEL_REASONS: Record<number, string> = {
  1: 'Cancelled by ASO.Market',
  2: 'Cancelled by us',
  3: 'App unavailable in the store',
  4: 'Unpaid balance',
};

/** done / checking / in work / waiting, as one bar. */
export function Progress({ c, size = 'sm' }: { c: Pick<AsomCampaign, 'total' | 'completed' | 'in_validation' | 'in_progress'>; size?: 'sm' | 'lg' }) {
  const total = Math.max(1, c.total);
  const pct = (n: number) => `${Math.min(100, (n / total) * 100)}%`;
  const h = size === 'lg' ? 'h-2.5' : 'h-1.5';
  return (
    <div className="min-w-[120px]">
      <div className={`flex ${h} rounded-full overflow-hidden bg-surface-muted`}>
        <div className="bg-success" style={{ width: pct(c.completed) }} />
        <div className="bg-warning" style={{ width: pct(c.in_validation) }} />
        <div className="bg-info" style={{ width: pct(c.in_progress) }} />
      </div>
      <div className="mt-1 text-xs text-content-muted tabular-nums">
        <span className="text-content font-medium">{Math.round(c.completed)}</span> / {c.total}
        {c.in_validation > 0 && <span className="text-warning"> · {Math.round(c.in_validation)} checking</span>}
        {c.in_progress > 0 && <span className="text-info"> · {Math.round(c.in_progress)} in work</span>}
      </div>
    </div>
  );
}

export function AppLogo({ src, name, size = 36 }: { src: string | null | undefined; name: string; size?: number }) {
  if (src) return <img src={src} alt="" width={size} height={size} className="rounded-lg object-cover bg-surface-muted shrink-0" style={{ width: size, height: size }} />;
  return (
    <div className="rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200 flex items-center justify-center font-semibold shrink-0" style={{ width: size, height: size }}>
      {(name || '?').slice(0, 1).toUpperCase()}
    </div>
  );
}

export function Stars({ value, onChange, size = 'md' }: { value: number; onChange?: (v: number) => void; size?: 'sm' | 'md' }) {
  const cls = size === 'sm' ? 'text-sm' : 'text-2xl';
  return (
    <span className={`inline-flex gap-0.5 ${cls}`} role={onChange ? 'radiogroup' : undefined}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n} type="button" disabled={!onChange} aria-label={`${n} stars`}
          onClick={() => onChange?.(n)}
          className={`${n <= value ? 'text-amber-400' : 'text-content-subtle/40'} ${onChange ? 'hover:scale-110 transition-transform' : 'cursor-default'}`}
        >★</button>
      ))}
    </span>
  );
}

export function Segmented<T extends string | number>({ value, onChange, options, nowrap }: {
  value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; hint?: string }[]; nowrap?: boolean;
}) {
  return (
    <div className={`inline-flex ${nowrap ? 'shrink-0' : 'flex-wrap'} rounded-lg bg-surface-muted p-0.5 gap-0.5`}>
      {options.map((o) => (
        <button
          key={String(o.value)} type="button" title={o.hint} onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${value === o.value ? 'bg-surface text-content shadow-sm font-medium' : 'text-content-muted hover:text-content'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ checked, onChange, label, hint, disabled }: {
  checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string; disabled?: boolean;
}) {
  return (
    <label className={`flex items-start gap-3 py-1.5 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <button
        type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)}
        className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-border-strong'}`}
      >
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
      <span>
        <span className="block text-sm text-content">{label}</span>
        {hint && <span className="block text-xs text-content-subtle">{hint}</span>}
      </span>
    </label>
  );
}

export const INPUT = 'block w-full px-3 py-2 border border-border-strong rounded-md text-sm bg-surface text-content placeholder-content-subtle focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary disabled:opacity-50';

export function FieldLabel({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2 mb-1.5">
      <span className="text-sm font-medium text-content">{children}</span>
      {aside && <span className="text-xs text-content-subtle">{aside}</span>}
    </div>
  );
}

export function Notice({ tone, children }: { tone: 'warning' | 'danger' | 'info' | 'success'; children: ReactNode }) {
  const cls = { warning: 'bg-warning-soft text-warning', danger: 'bg-danger-soft text-danger', info: 'bg-info-soft text-info', success: 'bg-success-soft text-success' }[tone];
  return <div className={`px-3 py-2 rounded-md text-sm ${cls}`}>{children}</div>;
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`bg-surface border border-border rounded-xl ${className}`}>{children}</div>;
}
